import { useEffect, useCallback, useState, useMemo } from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { Sensor, SensorAPI, DropResult } from 'react-beautiful-dnd';
import { useCollabStore } from '@atlassian/jira-business-collaboration';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous';
import { useBoardStore } from '../../services/board-store';
import type { Issue } from '../../services/board-store/types';
import { useRefetchBoardData as useRefetchBoardDataOld } from '../board-data-old';
import { useScrollElementStore } from '../scroll-element-old';
import { getAnimatedCardPosition, animateCard, getAnimationPoints, getDropParams } from './utils';

// how many animation points between start and end position
// the higher the number, the slower and smoother the animation
const ANIMATION_SPEED = 50;

// fetching board data in the end of animation sequence (that is when all animations have ended)
/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useSyncBoardState = () => {
	const [{ dragAnimation }] = useCollabStore();
	const [inProgress, setInProgress] = useState(false);
	const wasInProgress = usePreviousWithInitial(inProgress);

	const animation = dragAnimation[0];

	const projectData = useProject();
	const projectId = String(projectData.id);

	const refetchBoardData = useRefetchBoardDataOld();

	useEffect(() => {
		const isAnimationProgress = animation != null;
		if (isAnimationProgress !== inProgress) {
			setInProgress(isAnimationProgress);
		}
	}, [animation, inProgress]);

	useEffect(() => {
		if (inProgress === false && wasInProgress === true) {
			refetchBoardData();
		}
	}, [inProgress, projectId, refetchBoardData, wasInProgress]);
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useCardAnimationSensor: Sensor = (api: SensorAPI) => {
	const [{ dragAnimation }, { stopDragAnimation }] = useCollabStore();
	const [{ scrollElement }] = useScrollElementStore();

	const [{ columns }] = useBoardStore();
	const collapsedColumns = useMemo(
		() => columns.filter((c) => !!c.isCollapsed).map((c) => String(c.statusId)),
		[columns],
	);

	useSyncBoardState();

	// getting animations one-by-one from the store, until there's nothing to animate
	// we can only animate one card at a time
	// each animation is removed from store once finished
	const animation = dragAnimation[0];
	const sourceNodeId =
		animation && animation?.sourceNodeId ? `${animation?.sourceNodeId}` : undefined;

	const preDrag = useMemo(
		() =>
			!api.isLockClaimed() && sourceNodeId != null
				? api.tryGetLock(`ISSUE::${sourceNodeId}`)
				: null,
		[api, sourceNodeId],
	);

	useEffect(() => {
		if (!animation && api.isLockClaimed()) {
			api.tryReleaseLock();
		}
	}, [api, animation]);

	const position = useMemo(
		() => getAnimatedCardPosition({ animation, scrollElement, collapsedColumns }),
		[animation, scrollElement, collapsedColumns],
	);

	useEffect(() => {
		if (sourceNodeId == null) {
			return;
		}

		if (
			// couldn't get the lock, possibly because the sournce node is not in the DOM
			// this could happen when remote user updates virtualized card (outside viewport)
			!preDrag ||
			!position
		) {
			stopDragAnimation({ sourceNodeId });
			return;
		}

		const drag = preDrag.fluidLift(position.start);
		const points = getAnimationPoints({ ...position, count: ANIMATION_SPEED });

		requestAnimationFrame(() => {
			animateCard(drag, points);
		});
	}, [api, preDrag, scrollElement, stopDragAnimation, sourceNodeId, animation, position]);
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useCardAnimation = () => {
	const [
		{ participants, dragAnimation },
		{
			stopDragAnimation,
			clearNodeSelection,
			sendClearNodeSelection,
			sendNodeSelected,
			setNodeSelection,
		},
	] = useCollabStore();

	const onAnimationStart = useCallback(
		({ issueId, nodeId }: { issueId: number; nodeId: string }) => {
			// set telepointer of animated card for current user
			const animation = dragAnimation.find((a) => Number(a.sourceNodeId) === issueId);
			if (animation) {
				const remoteUser = participants.find(
					(participant) => participant.clientId === animation.userId,
				);
				if (remoteUser && remoteUser.clientId != null) {
					setNodeSelection({
						nodeId,
						sessionId: remoteUser.sessionId,
						user: {
							userId: remoteUser.clientId,
							name: remoteUser.name,
							email: remoteUser.email,
							avatar: remoteUser.avatar,
						},
					});
				}
			} else {
				// set telepointer for remote users
				sendNodeSelected({ nodeId });
			}
		},
		[dragAnimation, participants, sendNodeSelected, setNodeSelection],
	);

	const onAnimationEnd = useCallback(
		({
			issueId,
			nodeId,
			dragStop,
			issues,
		}: {
			issueId: number;
			nodeId: string;
			dragStop: DropResult;
			issues: Issue[];
		}) => {
			// clear telepointer of animated card for current user
			const animation = dragAnimation.find((a) => Number(a.sourceNodeId) === issueId);
			if (animation) {
				clearNodeSelection({ nodeId });
			} else {
				// clear telepointer for remote users
				sendClearNodeSelection({
					nodeId,
					dropParams: getDropParams({ issueId, issues, dragStop }),
				});
			}

			if (
				(dragStop?.reason === 'CANCEL' || dragStop?.destination == null) &&
				dragStop?.draggableId
			) {
				stopDragAnimation({ sourceNodeId: `${issueId}` });
			}
		},
		[clearNodeSelection, dragAnimation, sendClearNodeSelection, stopDragAnimation],
	);

	return { onAnimationStart, onAnimationEnd };
};
